// BodyMap — thin, theme-injected wrapper around react-native-body-highlighter.
//
// Lifted from apps/mobile into @jackedlogs/ui (2026-07-01) so web-rnw renders
// the real figure instead of a "view on mobile" placeholder. The only thing
// that was mobile-local was the theme source: this version takes `theme`
// (structurally a UiTheme — the mobile useTheme() object is assignable) and an
// `isDark` flag as props. The mobile side keeps a 5-line shim that injects
// useTheme(); web passes resolveTheme(dark, family) + the same dark flag, so
// both platforms share one figure and the grey→peak heatmapRamp renders on web.
//
// Canonical 20-slug API from @jackedlogs/shared. rnbh uses a single "deltoids"
// slug for both front and back; this wrapper translates our "front-deltoids" /
// "back-deltoids" into "deltoids" + the visible view, so the picker paints the
// front cap without confusing the lib's data model (and vice versa on the back).

import { useMemo } from "react";
import { View, StyleSheet, Pressable, Text } from "react-native";
import { useTranslation } from "react-i18next";
import * as RNBHModule from "react-native-body-highlighter";
import type {
  ExtendedBodyPart,
  Slug as RNBHSlug,
} from "react-native-body-highlighter";

// react-native-body-highlighter ships a CJS default export. Metro (mobile)
// resolves `import Body from` to the component directly. Vite/rolldown, however,
// wraps the CJS require with esbuild's __toESM(mod, /*isNodeMode*/ 1) — whose
// node-mode branch defines `.default` as the WHOLE module object instead of
// honouring the module's own `exports.default`. A plain default import is then
// the `{ default, __esModule }` object on web, and rendering it crashes with
// "element type is invalid" (React #130). Unwrap the interop defensively: this
// resolves to the Body component on both platforms (on Metro the first `.default`
// is already the component, so the nested `.default` is skipped).
const RNBHDefault = (RNBHModule as { default?: unknown }).default;
const Body = (((RNBHDefault as { default?: unknown } | undefined)?.default ??
  RNBHDefault ??
  RNBHModule) as typeof import("react-native-body-highlighter").default);

import {
  MUSCLE_VIEW,
  type BodyFigure,
  type BodyMapValue,
  type BodyView,
  type MuscleSlug,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";

export type BodyMapMode = "pick" | "display";

export type BodyMapProps = {
  mode: BodyMapMode;
  view: BodyView;
  value: BodyMapValue;
  // Theme is host-provided (mobile useTheme() / web resolveTheme). isDark picks
  // the figure border (borderStrong on dark, border on light) — UiTheme itself
  // doesn't carry the scheme name, so the flag comes alongside.
  theme: UiTheme;
  isDark?: boolean;
  onMusclePress?: (slug: MuscleSlug) => void;
  onViewToggle?: () => void;
  scale?: number;
  // Mapped to rnbh's gender prop ('feminine' → 'female'; anything else,
  // including absent, → 'male'). Consumer surfaces read profile.preferredFigure
  // and pass it through.
  figure?: BodyFigure;
  // 'single' (default) renders one figure and exposes the front/back toggle
  // when onViewToggle is provided. 'dual' renders front + back side-by-side and
  // drops the toggle (it would be a no-op when both views are visible).
  // Display-mode surfaces default to 'dual' for the full glance; pick-mode
  // surfaces stay 'single' so tap-targets keep the 44pt floor.
  layout?: "single" | "dual";
};

// Set of rnbh slugs we route to from our canonical names. Anything not in here
// (e.g. our "abductors", which rnbh doesn't render) gets dropped at mapping time.
const RNBH_DIRECT: ReadonlySet<RNBHSlug> = new Set<RNBHSlug>([
  "chest",
  "biceps",
  "triceps",
  "forearm",
  "trapezius",
  "upper-back",
  "lower-back",
  "abs",
  "obliques",
  "quadriceps",
  "hamstring",
  "gluteal",
  "adductors",
  "calves",
  "tibialis",
  "neck",
  "head",
]);

function toRNBHData(value: BodyMapValue, view: BodyView): ExtendedBodyPart[] {
  const out: ExtendedBodyPart[] = [];
  for (const [rawSlug, paint] of Object.entries(value)) {
    if (!paint) continue;
    const slug = rawSlug as MuscleSlug;
    if (slug === "front-deltoids") {
      if (view === "front") {
        out.push({
          slug: "deltoids",
          color: paint.colour,
          intensity: paint.intensity,
        });
      }
      continue;
    }
    if (slug === "back-deltoids") {
      if (view === "back") {
        out.push({
          slug: "deltoids",
          color: paint.colour,
          intensity: paint.intensity,
        });
      }
      continue;
    }
    if (slug === "abductors") {
      // rnbh has no abductors slug; the closest visual cue is the gluteal area
      // on the back view. Silently drop; picker UX relies on the
      // back-deltoids / gluteal taps for hip pain.
      continue;
    }
    if (RNBH_DIRECT.has(slug as RNBHSlug)) {
      // Hide neck on the front view if we're only painting the back (and vice
      // versa) — rnbh paints both sides if data is present, which would leak
      // picker selections across views.
      const sidedness = MUSCLE_VIEW[slug];
      if (sidedness !== "both" && sidedness !== view) continue;
      out.push({
        slug: slug as RNBHSlug,
        color: paint.colour,
        intensity: paint.intensity,
      });
    }
  }
  return out;
}

// Map rnbh's onBodyPartPress result back to our canonical slug. The only
// ambiguous case is "deltoids" — the current view decides front vs back cap.
function fromRNBHSlug(rnbhSlug: RNBHSlug, view: BodyView): MuscleSlug | null {
  if (rnbhSlug === "deltoids") {
    return view === "front" ? "front-deltoids" : "back-deltoids";
  }
  switch (rnbhSlug) {
    case "chest":
    case "biceps":
    case "triceps":
    case "forearm":
    case "trapezius":
    case "upper-back":
    case "lower-back":
    case "abs":
    case "obliques":
    case "quadriceps":
    case "hamstring":
    case "gluteal":
    case "adductors":
    case "calves":
    case "tibialis":
    case "neck":
    case "head":
      return rnbhSlug;
    default:
      return null;
  }
}

export function BodyMap({
  mode,
  view,
  value,
  theme,
  isDark = false,
  onMusclePress,
  onViewToggle,
  scale = 1.4,
  figure,
  layout = "single",
}: BodyMapProps) {
  const { t } = useTranslation();
  // rnbh's gender prop uses sex terms ('male' / 'female') while our schema uses
  // figure terms ('masculine' / 'feminine') decoupled from sex. Map at the
  // boundary; absent figure resolves to 'male' (the historical default).
  const gender = figure === "feminine" ? "female" : "male";

  // Dual-view halves the per-figure scale so the pair fits the same horizontal
  // slot a single-view would occupy. Consumers passing an explicit scale still
  // get it honoured — the 0.55 multiplier just becomes the new baseline.
  const isDual = layout === "dual";
  const perFigureScale = isDual ? scale * 0.55 : scale;
  const dataFront = useMemo(() => toRNBHData(value, "front"), [value]);
  const dataBack = useMemo(() => toRNBHData(value, "back"), [value]);
  const dataSingle = useMemo(() => toRNBHData(value, view), [value, view]);

  const renderFigure = (side: BodyView, bodyData: ExtendedBodyPart[]) => (
    <Body
      data={bodyData}
      side={side}
      scale={perFigureScale}
      gender={gender}
      defaultFill={theme.palette.surface2}
      defaultStroke={theme.palette.borderStrong}
      defaultStrokeWidth={1}
      border={isDark ? theme.palette.borderStrong : theme.palette.border}
      onBodyPartPress={
        mode === "pick" && onMusclePress
          ? (bp) => {
              if (!bp.slug) return;
              const slug = fromRNBHSlug(bp.slug, side);
              if (slug) onMusclePress(slug);
            }
          : undefined
      }
    />
  );

  return (
    <View style={styles.wrap}>
      <View style={isDual ? styles.bodyWrapDual : styles.bodyWrap}>
        {isDual ? (
          <>
            <View style={styles.bodyWrap}>{renderFigure("front", dataFront)}</View>
            <View style={styles.bodyWrap}>{renderFigure("back", dataBack)}</View>
          </>
        ) : (
          renderFigure(view, dataSingle)
        )}
      </View>
      {/* The front/back toggle has no meaning in dual-view (both are visible),
          so we hide it. Consumers stop passing onViewToggle when they switch to
          dual to drop the toggle state entirely. */}
      {!isDual && onViewToggle ? (
        <Pressable
          accessibilityRole="button"
          onPress={onViewToggle}
          style={({ pressed }) => [
            styles.viewToggle,
            {
              backgroundColor: theme.palette.surface,
              borderColor: theme.palette.border,
              opacity: pressed ? 0.7 : 1,
            },
          ]}
        >
          <Text style={[styles.viewToggleLabel, { color: theme.palette.ink }]}>
            {view === "front"
              ? `${t("bodymap.viewBack")} ›`
              : `‹ ${t("bodymap.viewFront")}`}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: "center",
    paddingVertical: 8,
  },
  bodyWrap: {
    alignItems: "center",
    justifyContent: "center",
  },
  bodyWrapDual: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: 8,
  },
  viewToggle: {
    marginTop: 12,
    paddingVertical: 8,
    paddingHorizontal: 16,
    minHeight: 44,
    minWidth: 110,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderRadius: 999,
  },
  viewToggleLabel: {
    fontSize: 14,
    fontWeight: "600",
    letterSpacing: 0.2,
  },
});
